import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ACTIVITY_COLORS } from './rtFormat';

/**
 * A log drawn on one canvas against a vertical axis that is either time or
 * depth, with the largest value at the top: the newest sample on a time log,
 * the deepest on a depth log. The real-time screens use it; the LAS viewer
 * has its own, richer one (screens/logview).
 */

export interface StripRow {
  /** Where the row sits on the axis: unix ms, or metres. */
  y: number;
  values: Record<string, number>;
  activity?: string;
}

export interface StripCurve {
  key: string;
  label: string;
  unit: string;
  color: string;
  min: number;
  max: number;
  dashed?: boolean;
}

export type StripTrack =
  | { kind: 'axis'; width: number; title: string }
  | { kind: 'activity'; width: number; title: string }
  | { kind: 'value'; width: number; title: string; key: string; decimals: number }
  | { kind: 'curves'; width: number; title: string; curves: StripCurve[] };

interface Props {
  rows: StripRow[];
  tracks: StripTrack[];
  /** The axis value at the top edge, and how much of it the height shows. */
  top: number;
  span: number;
  /** Grid lines: minor and major step, in axis units. */
  minor: number;
  major: number;
  formatY: (y: number) => string;
  /** Rows further apart than this break a curve: a gap in the data. */
  gap: number;
}


export function StripLog({ rows, tracks, top, span, minor, major, formatY, gap }: Props) {
  const box = useRef<HTMLDivElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hover, setHover] = useState<number | null>(null);

  const totalWidth = tracks.reduce((sum, t) => sum + t.width, 0);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const yOf = useCallback((v: number) => ((top - v) / span) * size.height, [top, span, size.height]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || size.height <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(totalWidth * dpr);
    c.height = Math.round(size.height * dpr);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, totalWidth, size.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, totalWidth, size.height);

    const bottom = top - span;
    // Rows on screen, and one either side so lines reach the edges.
    const visible = rows.filter((r, i) => {
      const next = rows[i + 1];
      const prev = rows[i - 1];
      return (r.y >= bottom && r.y <= top) || (next && next.y >= bottom && r.y < bottom) || (prev && prev.y <= top && r.y > top);
    });

    let x0 = 0;
    for (const track of tracks) {
      const w = track.width;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, 0, w, size.height);
      ctx.clip();

      if (track.kind !== 'activity') {
        for (let k = Math.ceil(bottom / minor); k * minor <= top; k++) {
          const v = k * minor;
          const isMajor = Math.abs(v / major - Math.round(v / major)) < 1e-6;
          ctx.strokeStyle = isMajor ? '#c3ccd3' : '#eceff2';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x0, Math.round(yOf(v)) + 0.5);
          ctx.lineTo(x0 + w, Math.round(yOf(v)) + 0.5);
          ctx.stroke();
        }
      }

      if (track.kind === 'axis' || track.kind === 'value') {
        ctx.fillStyle = track.kind === 'axis' ? '#24313a' : '#1f3fbf';
        ctx.font = '10.5px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (let k = Math.ceil(bottom / major); k * major <= top; k++) {
          const v = k * major;
          let label = formatY(v);
          if (track.kind === 'value') {
            const near = nearest(rows, v);
            if (!near || Math.abs(near.y - v) > gap) continue;
            label = (near.values[track.key] ?? NaN).toFixed(track.decimals);
          }
          const tw = ctx.measureText(label).width;
          ctx.save();
          ctx.translate(x0 + w / 2, yOf(v));
          if (w < 50) ctx.rotate(-Math.PI / 2);
          ctx.fillStyle = '#fff';
          ctx.fillRect(-tw / 2 - 2, -6, tw + 4, 12);
          ctx.fillStyle = track.kind === 'axis' ? '#24313a' : '#1f3fbf';
          ctx.fillText(label, 0, 0);
          ctx.restore();
        }
      }

      if (track.kind === 'activity') {
        for (let i = 0; i < visible.length; i++) {
          const r = visible[i];
          const next = visible[i + 1];
          const yTop = yOf(r.y);
          const yBottom = next && r.y - next.y <= gap ? yOf(next.y) : yTop + 2;
          ctx.fillStyle = ACTIVITY_COLORS[r.activity ?? ''] ?? '#eceff2';
          ctx.fillRect(x0, Math.min(yTop, yBottom), w, Math.max(1, Math.abs(yBottom - yTop)));
        }
      }

      if (track.kind === 'curves') {
        for (let i = 1; i < 10; i++) {
          ctx.strokeStyle = i === 5 ? '#d3dae0' : '#eceff2';
          ctx.beginPath();
          ctx.moveTo(Math.round(x0 + (i * w) / 10) + 0.5, 0);
          ctx.lineTo(Math.round(x0 + (i * w) / 10) + 0.5, size.height);
          ctx.stroke();
        }
        for (const c of track.curves) {
          ctx.strokeStyle = c.color;
          ctx.lineWidth = 1.3;
          ctx.setLineDash(c.dashed ? [5, 3] : []);
          ctx.beginPath();
          let pen = false;
          let prevY = 0;
          for (const r of visible) {
            const v = r.values[c.key];
            if (v === undefined || Number.isNaN(v)) {
              pen = false;
              continue;
            }
            const f = (v - c.min) / (c.max - c.min || 1);
            const px = x0 + Math.min(1, Math.max(0, f)) * w;
            const py = yOf(r.y);
            if (pen && Math.abs(prevY - r.y) <= gap) ctx.lineTo(px, py);
            else ctx.moveTo(px, py);
            pen = true;
            prevY = r.y;
          }
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      ctx.restore();
      x0 += w;
    }

    ctx.strokeStyle = '#6b7882';
    ctx.lineWidth = 1;
    ctx.beginPath();
    let edge = 0;
    for (const t of tracks) {
      edge += t.width;
      ctx.moveTo(Math.round(edge) - 0.5, 0);
      ctx.lineTo(Math.round(edge) - 0.5, size.height);
    }
    ctx.stroke();
  }, [rows, tracks, top, span, minor, major, formatY, gap, size, totalWidth, yOf]);

  const hovered = hover === null ? null : nearest(rows, top - (hover / Math.max(1, size.height)) * span);

  return (
    <div className="strip">
      <div className="strip-head" style={{ width: totalWidth }}>
        {tracks.map((t, i) => (
          <div key={i} className={t.width < 50 ? 'strip-headcell strip-headcell--vertical' : 'strip-headcell'} style={{ width: t.width }}>
            <span className="strip-title">{t.title}</span>
            {t.kind === 'curves' &&
              t.curves.map((c) => (
                <span className="strip-legend" key={c.key}>
                  <span style={{ color: c.color }}>
                    {c.label} <small>{c.unit}</small>
                  </span>
                  <span className="strip-scale">
                    <span>{c.min}</span>
                    <i style={{ borderTopColor: c.color, borderTopStyle: c.dashed ? 'dashed' : 'solid' }} />
                    <span>{c.max}</span>
                  </span>
                </span>
              ))}
          </div>
        ))}
      </div>
      <div
        className="strip-body"
        ref={box}
        onMouseMove={(e) => setHover(e.clientY - e.currentTarget.getBoundingClientRect().top)}
        onMouseLeave={() => setHover(null)}
      >
        <canvas ref={canvas} style={{ width: totalWidth, height: size.height }} />
        {hover !== null && hovered && (
          <>
            <div className="strip-crosshair" style={{ top: hover, width: totalWidth }} aria-hidden="true" />
            <div className="strip-readout" style={{ top: Math.max(0, hover - 34) }}>
              <strong>{formatY(hovered.y)}</strong>
              {hovered.activity && <span>{hovered.activity}</span>}
              {tracks.flatMap((t) =>
                t.kind === 'curves'
                  ? t.curves.map((c) => (
                      <span key={c.key} style={{ color: c.color }}>
                        {c.label} {hovered.values[c.key]?.toFixed(1)}
                      </span>
                    ))
                  : [],
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function nearest(rows: StripRow[], y: number): StripRow | null {
  if (rows.length === 0) return null;
  // Rows run in increasing y; a binary search finds the closest.
  let lo = 0;
  let hi = rows.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (rows[mid].y <= y) lo = mid;
    else hi = mid;
  }
  return Math.abs(rows[lo].y - y) <= Math.abs(rows[hi].y - y) ? rows[lo] : rows[hi];
}
